import React, {useMemo, useState} from "react";
import {NodeType} from "../../../model/ModelData";
import {IconText} from "../../../assets/icons/IconText";
import {
    IconArrowsSplit2,
    IconBrandJavascript,
    IconChevronDown,
    IconChevronRight,
    IconChevronUp,
    IconKeyboard,
    IconLogicAnd,
    IconMessage2,
    IconMessage2Question,
    IconPhoneOutgoing,
    IconRobot,
    IconSitemap,
    IconVariable,
    IconWand,
    IconWriting
} from "@tabler/icons-react";
import {IconAI} from "../../../assets/icons/IconAI";
import {IconHook} from "../../../assets/icons/IconHook";
import {IconExit} from "../../../assets/icons/IconExit";
import {IconSwitch} from "../../../assets/icons/IconSwitch";
import {Box, Button, Fade, Menu, MenuItem, Stack, Tooltip, Typography} from "@mui/material";
import {Debugger} from "./debugger/Debugger";
import {IconQA} from "../../../assets/icons/IconQA";
import {hexToRgba, NODE_SHADOW, NODE_SHADOW_HOVER} from "../../../nodes/nodeTheme";

const ACCENT = "#6366F1";
const INK = "#1E293B";
const MUTED = "#64748B";

export const NodeMenu = ({addNode, openChat, updatePosition, useLLM, onToggleLLM}) => {
    const [showMore, setShowMore] = useState(true);
    const nodeItems = useMemo(() => {
        return [
            {
                type: "input",
                label: "Input",
                icon: <IconKeyboard size={22} strokeWidth={1.4}/>,
                items: [
                    {type: NodeType.INPUT, label: "Text", icon: <IconWriting size={20} stroke={1.4}/>},
                    {type: NodeType.QUERY, label: "Query", icon: <IconMessage2Question stroke={1.4} size={20}/>},
                    {type: NodeType.CHOICE, label: "Choice", icon: <IconArrowsSplit2 size={20} stroke={1.4}/>},
                ]
            },
            {
                type: "output",
                label: "Output",
                icon: <IconMessage2 size={22} strokeWidth={1.4}/>,
                items: [
                    {type: NodeType.TEXT, label: "Text", icon: <IconText size={22} color={"currentColor"}/>},
                    {
                        type: NodeType.JAVASCRIPT,
                        label: "Javascript",
                        icon: <IconBrandJavascript size={22} strokeWidth={1.4}/>
                    },
                ]
            },
            {
                type: "use_llm",
                label: <>LLM<br/>Booster</>,
                title: "LLM Booster",
                icon: <IconWand size={20} strokeWidth={1.4}/>
            },
            {
                type: "logic",
                label: "Logic",
                icon: <IconLogicAnd size={22} strokeWidth={1.4}/>,
                items: [
                    {type: NodeType.SWITCH, label: "Switch", icon: <IconSwitch size={18} color={"currentColor"}/>},
                    {type: NodeType.SET_VAR, label: "Set", icon: <IconVariable size={22} strokeWidth={1.4}/>},
                ]
            },
            {
                type: "ai",
                label: "AI",
                icon: <IconAI size={22} color={"currentColor"}/>,
                items: [
                    {type: NodeType.AGENT, label: "Agent", icon: <IconRobot size={22} strokeWidth={1.4}/>},
                    {
                        type: NodeType.CHATBOT,
                        label: "Chatbot",
                        icon: <IconMessage2Question size={22} strokeWidth={1.4}/>
                    },
                    {
                        type: NodeType.GEN_AI_WORKFLOW,
                        label: "GenAI Workflow",
                        icon: <IconRobot size={22} strokeWidth={1.4}/>
                    },
                    {type: NodeType.QA, label: "QA", icon: <IconQA size={22} color={"currentColor"}/>},
                ]
            },
            {
                type: NodeType.CALL_TRANSFER,
                label: "Transfer",
                icon: <IconPhoneOutgoing size={22} strokeWidth={1.4}/>
            },
            {type: NodeType.HOOK, label: "Hook", icon: <IconHook size={22} color={"currentColor"}/>},
            {type: NodeType.START_FLOW, label: "Flow", icon: <IconSitemap size={22} strokeWidth={1.4}/>},
            {type: NodeType.END, label: "End", icon: <IconExit size={22} color={"currentColor"}/>},
        ]
    }, [])

    if (openChat) {
        return <Debugger open={openChat} updatePosition={updatePosition}/>;
    }

    const visibleItems = nodeItems.slice(0, showMore ? nodeItems.length : 6);

    return (
        <Box
            sx={{
                position: "absolute",
                top: 12,
                left: 12,
                bgcolor: "#FFFFFF",
                border: `1px solid ${hexToRgba(ACCENT, 0.16)}`,
                borderRadius: "14px",
                boxShadow: NODE_SHADOW,
                zIndex: 990,
                p: 0.5,
                width: 58,
            }}
        >
            <Stack direction={"column"} gap={0.25}>
                {visibleItems.map(item => (
                    <NodeMenuItem
                        key={item.type}
                        icon={item.icon}
                        items={item.items || []}
                        title={item.title || ""}
                        type={item.type}
                        useLLM={useLLM}
                        onToggleLLM={onToggleLLM}
                        label={item.label}
                        addNode={addNode}
                    />
                ))}

                <Box sx={{height: "1px", bgcolor: hexToRgba(ACCENT, 0.12), mx: 0.5, my: 0.25}}/>

                <Tooltip placement={"right"} title={showMore ? "Show less" : "Show more"}>
                    <Button
                        disableRipple
                        onClick={() => setShowMore(!showMore)}
                        sx={{
                            minWidth: 0,
                            width: "100%",
                            py: 0.5,
                            borderRadius: "9px",
                            color: MUTED,
                            transition: "background-color .15s ease, color .15s ease",
                            "&:hover": {bgcolor: hexToRgba(ACCENT, 0.08), color: INK},
                        }}
                    >
                        {showMore ? <IconChevronUp size={18}/> : <IconChevronDown size={18}/>}
                    </Button>
                </Tooltip>
            </Stack>
        </Box>
    );
}

const NodeMenuItem = ({type, label, title, icon, items = [], useLLM, addNode, onToggleLLM}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const hasChildren = items.length > 0;

    const handleClick = (event) => {
        if (hasChildren) {
            setAnchorEl(event.currentTarget);
        } else if (type === "use_llm") {
            onToggleLLM();
        } else {
            addNode(type);
        }
    };
    const handleClose = (selectedType) => {
        if (Object.values(NodeType)?.includes(selectedType)) {
            addNode(selectedType);
        }
        if (selectedType === "use_llm") {
            onToggleLLM();
        }
        setAnchorEl(null);
    };

    const active = (type === "use_llm" && useLLM) || open;

    return (
        <>
            <Fade in timeout={250}>
                <Tooltip placement={"right"} title={`Create ${title || label} node`}>
                    <Button
                        disableRipple
                        id="fade-button"
                        aria-controls={open ? "fade-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                        sx={{
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.25,
                            alignItems: "center",
                            justifyContent: "center",
                            textTransform: "none",
                            minWidth: 0,
                            width: "100%",
                            py: 0.5,
                            px: 0,
                            borderRadius: "9px",
                            color: active ? ACCENT : MUTED,
                            bgcolor: active ? hexToRgba(ACCENT, 0.1) : "transparent",
                            transition: "background-color .15s ease, color .15s ease",
                            "& svg": {transition: "color .15s ease, stroke .15s ease"},
                            "&:hover": {
                                bgcolor: hexToRgba(ACCENT, active ? 0.14 : 0.08),
                                color: active ? ACCENT : INK,
                            },
                        }}
                    >
                        {hasChildren && (
                            <IconChevronRight
                                size={11}
                                style={{position: "absolute", top: 4, right: 4, opacity: 0.55}}
                            />
                        )}
                        {icon}
                        <Typography
                            noWrap
                            sx={{
                                fontSize: 9.5,
                                lineHeight: 1.15,
                                fontWeight: active ? 600 : 500,
                                letterSpacing: "0.01em",
                                color: "inherit",
                                textAlign: "center",
                            }}
                        >
                            {label}
                        </Typography>
                    </Button>
                </Tooltip>
            </Fade>

            {hasChildren && open && (
                <Menu
                    id="fade-menu"
                    MenuListProps={{"aria-labelledby": "fade-button", dense: true, sx: {py: 0.5}}}
                    anchorEl={anchorEl}
                    anchorOrigin={{vertical: "top", horizontal: "right"}}
                    transformOrigin={{vertical: "top", horizontal: "left"}}
                    sx={{ml: 1}}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: "12px",
                                border: `1px solid ${hexToRgba(ACCENT, 0.16)}`,
                                boxShadow: NODE_SHADOW_HOVER,
                                px: 0.5,
                            },
                        },
                    }}
                    open={open}
                    onClose={handleClose}
                    TransitionComponent={Fade}
                >
                    {items.map(item => (
                        <MenuItem
                            key={item.type}
                            onClick={() => handleClose(item.type)}
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 1.25,
                                minWidth: 168,
                                borderRadius: "8px",
                                py: 0.5,
                                my: 0.15,
                                color: MUTED,
                                "& svg": {stroke: ACCENT, color: ACCENT},
                                "&:hover": {bgcolor: hexToRgba(ACCENT, 0.08), color: INK},
                            }}
                        >
                            {item.icon}
                            <Typography noWrap sx={{fontSize: 12.5, fontWeight: 500, color: "inherit"}}>
                                {item.label}
                            </Typography>
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </>
    )
}
